import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const LSYSTEM_TREE_PARAMS = [
  // ── Wuchs ─────────────────────────────────────────────────────────────────
  {
    kind: 'int',
    key: 'depth',
    label: 'Rekursionstiefe',
    min: 1,
    max: 12,
    step: 1,
    // Eight levels with two children is 256 tips — enough for a full crown, few
    // enough that the letters stay individually readable rather than merging.
    default: 8,
    bulkRange: [6, 10],
    group: 'Wuchs',
    tooltip: 'Wie viele Verzweigungsebenen der Baum bildet.',
  },
  {
    kind: 'int',
    key: 'children',
    label: 'Kindäste',
    min: 1,
    max: 5,
    step: 1,
    default: 2,
    group: 'Wuchs',
    tooltip:
      'Wie viele Äste an jeder Gabelung entstehen. Die Gesamtzahl ist bei 60 000 Ästen gedeckelt — sonst wären es bei Tiefe 12 und fünf Kindästen 244 Millionen.',
  },
  {
    kind: 'float',
    key: 'angle',
    label: 'Verzweigungswinkel',
    min: 2,
    max: 90,
    step: 0.5,
    default: 31,
    unit: '°',
    bulkRange: [12, 48],
    group: 'Wuchs',
  },
  {
    kind: 'float',
    key: 'angleJitter',
    label: 'Winkelstreuung',
    min: 0,
    max: 60,
    step: 0.5,
    default: 9,
    unit: '°',
    bulkRange: [0, 26],
    group: 'Wuchs',
  },
  {
    kind: 'float',
    key: 'asymmetry',
    label: 'Asymmetrie',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.18,
    group: 'Wuchs',
    tooltip: 'Wie unterschiedlich lang die Geschwisteräste ausfallen.',
  },
  {
    kind: 'float',
    key: 'lengthRatio',
    label: 'Längenverhältnis',
    min: 0.4,
    max: 0.98,
    step: 0.005,
    default: 0.78,
    bulkRange: [0.62, 0.88],
    group: 'Wuchs',
    tooltip: 'Wie viel kürzer jede Astebene gegenüber der vorherigen ist.',
  },
  {
    kind: 'float',
    key: 'trunkLength',
    label: 'Stammlänge',
    min: 0.05,
    max: 0.6,
    step: 0.005,
    default: 0.19,
    unit: '×H',
    group: 'Wuchs',
    tooltip: 'Länge des Stamms als Anteil der Canvas-Höhe.',
  },
  {
    kind: 'float',
    key: 'gravity',
    label: 'Gravitations-Bias',
    min: -1,
    max: 1,
    step: 0.01,
    default: -0.12,
    group: 'Wuchs',
    tooltip: 'Negativ streckt die Äste nach oben, positiv lässt sie hängen.',
  },
  {
    kind: 'vec2',
    key: 'root',
    label: 'Stammposition',
    min: { x: 0, y: 0 },
    max: { x: 1, y: 1 },
    step: 0.005,
    default: { x: 0.5, y: 0.98 },
    square: false,
    group: 'Wuchs',
    tooltip: 'Ansatzpunkt des Stamms, als Anteil der Canvas-Grösse.',
  },

  // ── Äste ──────────────────────────────────────────────────────────────────
  {
    kind: 'float',
    key: 'thickness',
    label: 'Startdicke',
    min: 0.5,
    max: 60,
    step: 0.5,
    default: 15,
    unit: 'px',
    bulkRange: [4, 34],
    group: 'Äste',
  },
  {
    kind: 'float',
    key: 'thicknessFalloff',
    label: 'Dicken-Abnahme',
    min: 0.4,
    max: 0.98,
    step: 0.005,
    default: 0.7,
    group: 'Äste',
    tooltip: 'Wie stark jede Ebene dünner wird als die vorherige.',
  },
  {
    kind: 'bool',
    key: 'avoidText',
    label: 'Text aussparen',
    default: false,
    group: 'Äste',
    tooltip: 'Äste, die in die Buchstaben laufen, brechen dort ab.',
  },

  // ── Blätter ───────────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'leafContent',
    label: 'Blattinhalt',
    default: 'letter',
    segmented: true,
    group: 'Blätter',
    options: [
      { value: 'letter', label: 'Buchstabe' },
      { value: 'word', label: 'Ganzes Wort' },
    ],
    tooltip: 'Ein einzelner Buchstabe pro Astspitze oder der komplette Text.',
  },
  {
    kind: 'float',
    key: 'leafChance',
    label: 'Blattbelegung',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.58,
    group: 'Blätter',
    tooltip: 'Anteil der Astspitzen, die ein Blatt tragen.',
  },
  {
    kind: 'float',
    key: 'leafSize',
    label: 'Blattgrösse',
    min: 4,
    max: 400,
    step: 1,
    scale: 'log',
    default: 46,
    unit: 'px',
    bulkRange: [16, 140],
    group: 'Blätter',
  },
  {
    kind: 'float',
    key: 'leafSizeJitter',
    label: 'Grössenstreuung',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.35,
    group: 'Blätter',
  },
  {
    kind: 'enum',
    key: 'leafRotation',
    label: 'Blattdrehung',
    default: 'branch',
    segmented: true,
    group: 'Blätter',
    options: [
      { value: 'upright', label: 'Aufrecht' },
      { value: 'branch', label: 'Astrichtung' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'float',
    key: 'leafRotationJitter',
    label: 'Drehstreuung',
    min: 0,
    max: 180,
    step: 1,
    default: 16,
    unit: '°',
    group: 'Blätter',
  },
  {
    kind: 'float',
    key: 'fallenRatio',
    label: 'Gefallene Blätter',
    min: 0,
    max: 0.8,
    step: 0.01,
    default: 0.12,
    group: 'Blätter',
    tooltip: 'Anteil der Blätter, die stattdessen am Boden liegen.',
  },
  {
    kind: 'float',
    key: 'groundLevel',
    label: 'Bodenhöhe',
    min: 0.3,
    max: 1,
    step: 0.005,
    default: 0.96,
    unit: '×H',
    group: 'Blätter',
    showIf: (v) => (v.fallenRatio as number) > 0,
  },

  // ── Darstellung ───────────────────────────────────────────────────────────
  {
    kind: 'bool',
    key: 'grow',
    label: 'Wachstumsanimation',
    default: true,
    group: 'Darstellung',
    tooltip: 'Der Baum baut sich Ebene für Ebene auf, statt sofort fertig dazustehen.',
  },
  {
    kind: 'float',
    key: 'growSpeed',
    label: 'Wachstumsdauer',
    min: 0.2,
    max: 8,
    step: 0.1,
    default: 1.6,
    unit: 's',
    neverRandomise: true,
    group: 'Darstellung',
    showIf: (v) => v.grow === true,
  },
  {
    kind: 'float',
    key: 'leafOpacity',
    label: 'Blattdeckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 1,
    group: 'Darstellung',
  },

  // ── Farbe ─────────────────────────────────────────────────────────────────
  {
    kind: 'color',
    key: 'trunkColor',
    label: 'Stammfarbe',
    default: '#4a3b2a',
    group: 'Farbe',
  },
  {
    kind: 'color',
    key: 'branchTipColor',
    label: 'Astspitzenfarbe',
    default: '#9c8256',
    group: 'Farbe',
  },
  {
    kind: 'palette',
    key: 'leafPalette',
    label: 'Blattpalette',
    default: { ...DEFAULT_PALETTE, source: 'curated', paletteId: 'ember' },
    group: 'Farbe',
    tooltip: 'Jedes Blatt zieht eine Farbe aus dieser Palette.',
  },
] as const satisfies ParamSchema;

export type LSystemTreeValues = SchemaValues<typeof LSYSTEM_TREE_PARAMS>;
